import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import SchemaOrg from "@/components/base/SchemaOrg";
import ScrollToTop from "@/components/base/ScrollToTop";
import ArticleCard from "@/components/feature/ArticleCard";
import FAQ from "@/components/feature/FAQ";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

const faqItems = [
  {
    question: "什么是打屁股SP？",
    answer: "打屁股SP是指在成人之间，基于知情、自愿、安全的前提下，以打屁股为形式的亲密互动活动。SP并非暴力行为，而是建立在信任、沟通和相互尊重基础上的身体体验。核心要素包括知情同意、安全保障、沟通信任和边界设定。"
  },
  {
    question: "SP活动安全吗？如何确保安全？",
    answer: "SP安全的前提是严格遵守安全原则：避开肾脏、尾骨、脊柱等危险区域，力度由轻到重循序渐进，设定并遵守安全词机制，控制活动时间，确保工具完好无缺。同时，心理安全和隐私保护同样重要，参与者应在活动前充分沟通并确认双方状态。"
  },
  {
    question: "新手应该如何开始SP体验？",
    answer: "新手建议从建立正确认知开始：首先了解SP的基础知识和安全原则，然后从最轻的力度、最简单的手掌工具开始，首次体验时间控制在15分钟以内。重要的是与伴侣充分沟通，建立信任，不要急于求成。逐步积累经验后再尝试不同工具和技巧。"
  },
  {
    question: "如何选择安全词？",
    answer: "安全词应选择简单易记、与SP语境完全无关的词语。推荐使用交通灯系统（红色=停止，黄色=减速，绿色=继续），或者选择双方都有意义的水果名称、日常物品名等。安全词一旦被说出，所有活动必须立即停止，无需解释原因。"
  },
  {
    question: "SP后需要做什么护理？",
    answer: "SP后的护理（Aftercare）至关重要。身体方面：如红肿可用冷敷，涂抹温和的润肤霜，保持皮肤清洁；情感方面：通过拥抱、交流给予彼此情感支持，讨论本次体验的感受和可改进之处。建议预留至少30分钟的事后关怀时间。"
  }
];

const featuredArticles = articles.filter((a) => a.featured);
const latestArticles = articles.slice().sort((a, b) => b.date.localeCompare(a.date)).slice(0, 6);

export default function HomePage() {
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "打屁股SP完全指南",
    "url": "https://sp-guide.readdy.ai/",
    "description": "打屁股SP完全指南，系统介绍SP定义、安全须知、工具选择、技巧方法及注意事项，帮助读者在安全、知情、自愿的前提下获取正确的SP相关知识。",
    "inLanguage": "zh-CN"
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "SP完全指南",
    "url": "https://sp-guide.readdy.ai/",
    "description": "致力于传播科学、安全、理性的SP知识。"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="打屁股SP完全指南｜安全姿势与注意事项 — SP知识科普平台"
        description="打屁股SP完全指南，系统介绍SP定义、安全须知、工具选择、技巧方法及注意事项。涵盖SP基础知识、安全原则、新手指南等全方位内容，帮助读者在安全、知情、自愿的前提下获取正确的SP相关知识。"
        keywords="打屁股sp,sp打屁股技巧,sp安全须知,sp工具选择,成人sp知识,sp注意事项,sp新手指南"
        canonicalUrl="https://sp-guide.readdy.ai/"
        ogTitle="打屁股SP完全指南｜安全姿势与注意事项"
        ogDescription="系统介绍SP定义、安全须知、工具选择及注意事项。涵盖基础知识、安全原则、新手指南等全方位内容。"
      />
      <SchemaOrg data={websiteSchema} />
      <SchemaOrg data={organizationSchema} />
      <SchemaOrg data={faqSchema} />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20elegant%20interior%20with%20soft%20amber%20lighting%2C%20cream%20walls%2C%20cozy%20reading%20nook%20with%20books%2C%20calm%20atmosphere%2C%20editorial%20photography%20style%20with%20warm%20golden%20tones%20and%20gentle%20shadows%2C%20sophisticated%20composition&width=1800&height=800&seq=hero-main-2026&orientation=landscape"
              alt="SP完全指南"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 md:px-6 w-full">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 mb-6">
                <i className="ri-shield-check-line text-background-50 text-sm"></i>
                <span className="text-xs font-medium text-background-50">安全 · 知情 · 自愿</span>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4 max-w-3xl leading-tight">
                打屁股SP完全指南
              </h1>
              <p className="text-sm md:text-lg text-background-50/85 max-w-2xl mb-8 leading-relaxed">
                系统介绍SP定义、安全须知、工具选择、技巧方法及注意事项，帮助您全面了解打屁股SP的相关知识
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <Link
                  to="/category/sp-beginner"
                  className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  新手指南
                </Link>
                <Link
                  to="/category/sp-safety"
                  className="px-6 py-3 rounded-lg bg-background-50/15 backdrop-blur-sm text-background-50 font-medium text-sm border border-background-50/30 hover:bg-background-50/25 transition-colors whitespace-nowrap"
                >
                  安全须知
                </Link>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  探索SP知识分类
                </h2>
                <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                  从基础知识到进阶技巧，六大分类助您系统学习SP相关知识
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="group bg-background-50 rounded-xl border border-background-200/60 overflow-hidden hover:border-primary-200/60 transition-all duration-300"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={cat.imageUrl}
                        alt={cat.name}
                        title={cat.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                          <i className={`${cat.icon} text-primary-600 text-lg`}></i>
                        </div>
                        <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                          {cat.name}
                        </h3>
                      </div>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        {cat.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Articles */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                    精选文章
                  </h2>
                  <p className="text-sm text-foreground-500">编辑推荐的高质量SP知识文章</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {featuredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-sm md:text-base text-foreground-500">
                  关于SP，您可能想了解的问题都在这里
                </p>
              </div>
              <FAQ items={faqItems} />
            </div>
          </section>

          {/* Latest Articles */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                    最新文章
                  </h2>
                  <p className="text-sm text-foreground-500">持续更新的SP知识内容</p>
                </div>
                <Link
                  to="/category/sp-basics"
                  className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative w-full py-16 md:py-24 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20elegant%20bookshelf%20with%20soft%20golden%20lighting%2C%20cream%20walls%2C%20cozy%20reading%20atmosphere%2C%20editorial%20photography%20with%20warm%20amber%20tones%20and%20gentle%20shadows%2C%20sophisticated%20interior&width=1800&height=600&seq=cta-bottom-2026&orientation=landscape"
              alt="了解更多SP知识"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40"></div>
            <div className="relative z-10 max-w-2xl mx-auto text-center px-4">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
                准备好深入了解SP了吗？
              </h2>
              <p className="text-sm md:text-base text-background-50/80 mb-8">
                从新手指南开始，逐步探索SP的世界。安全、知情、自愿是永恒的原则。
              </p>
              <Link
                to="/category/sp-beginner"
                className="inline-flex px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                开始学习
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}